import { BOGO, SPECIAL_BUYS } from '../constants';

export const getExperienceTag = ({ promotion }) => {
  return promotion?.experienceTag;
};

export const getSubExperienceTag = ({ promotion }) => {
  return promotion?.subExperienceTag;
};

export const getShortDescription = ({ promotion }) => {
  return promotion?.description?.shortDesc;
};

export const getLongDescription = ({ promotion }) => {
  return promotion?.description?.longDesc;
};

export const getStartDate = ({ promotion }) => {
  return promotion?.dates?.start;
};

export const getEndDate = ({ promotion }) => {
  return promotion?.dates?.end;
};

const hasShortDescription = ({ promotion }) => {
  return !!getShortDescription({ promotion });
};

export const getConditionalPromo = ({ data }) => {
  return data?.product?.pricing?.conditionalPromotions?.[0];
};

export const getDiscountPromo = ({ data }) => {
  return data?.product?.pricing?.promotion;
};

export const getSpecialBuy = ({ promotion }) => {
  const specialBuyKey = getShortDescription({ promotion });
  return SPECIAL_BUYS[specialBuyKey];
};

export const hasConditionalPromo = ({ data }) => {
  const conditionalPromo = getConditionalPromo({ data });
  return hasShortDescription({ promotion: conditionalPromo });
};

export const hasDiscountPromo = ({ data }) => {
  const discountPromo = getDiscountPromo({ data });
  return hasShortDescription({ promotion: discountPromo });
};

export const hasDiscountMatchingConditional = ({ data }) => {
  const discountPromo = getDiscountPromo({ data });
  const conditionalPromo = getConditionalPromo({ data });
  const discountExperienceTag = getExperienceTag({ promotion: discountPromo });
  const discountSubExperienceTag = getSubExperienceTag({ promotion: discountPromo });
  const conditionalExperienceTag = getExperienceTag({ promotion: conditionalPromo });
  const conditionalSubExperienceTag = getSubExperienceTag({ promotion: conditionalPromo });

  return (discountExperienceTag === conditionalExperienceTag
    && discountSubExperienceTag === conditionalSubExperienceTag);
};

export const hasPreferredPrice = ({ data }) => {
  return !!(data?.product?.pricing?.preferredPriceFlag);
};

export const hasSpecialBuy = ({ promotion }) => {
  const specialBuyKey = getShortDescription({ promotion });
  return Object.keys(SPECIAL_BUYS).includes(specialBuyKey);
};

export const isInHiddenPromos = ({
  data, hiddenPromos
}) => {
  const conditionalPromo = getConditionalPromo({ data });
  const experienceTag = getExperienceTag({ promotion: conditionalPromo });
  const subExperienceTag = getSubExperienceTag({ promotion: conditionalPromo });

  return hiddenPromos.some((hiddenPromo) => {
    if (experienceTag === BOGO) {
      return (hiddenPromo === subExperienceTag);
    }
    return (hiddenPromo === experienceTag);
  });
};

/*
  Business priority logic for selecting promotion data to display:
  1. Special Buys
  2. unhidden conditional promos that have short descs
  3. any other promo with short description
    a. Coupons or other PE promos with short descriptions
    b. any remaining promo short desc
*/
export const getPromoToDisplay = ({ data, hiddenPromos, hideConditionalPromoDescription }) => {
  const isPreferredPrice = hasPreferredPrice({ data });
  const isSpecialBuy = hasSpecialBuy({ promotion: getDiscountPromo({ data }) });
  const isConditionalPromotion = hasConditionalPromo({ data });
  const isHiddenConditionalPromo = hideConditionalPromoDescription && isInHiddenPromos({ data, hiddenPromos });
  const isHiddenDiscountPromo = isHiddenConditionalPromo && hasDiscountMatchingConditional({ data });

  if (isPreferredPrice) return null;

  if (isSpecialBuy) return getDiscountPromo({ data });

  // includes conditionalPromotions with an experienceTag
  if (isConditionalPromotion && !isHiddenConditionalPromo) {
    return getConditionalPromo({ data });
  }

  // includes coupons and discounts and conditional promos without an experienceTag
  if (hasDiscountPromo({ data }) && !isHiddenDiscountPromo) {
    return getDiscountPromo({ data });
  }

  return null;
};
